<template>
    <!-- top-right matches where vue3-toastify put notifications before the
         re-skin; Nuxt UI defaults to the bottom, where they collide with the
         footer and are easy to miss on data-heavy pages. -->
    <UApp :locale="uiLocale" :toaster="{ position: 'top-right' }">
        <LoaderComponent v-if="appStore.isLoaderActive()" />
        <component :is="layout">
            <RouterView />
        </component>
    </UApp>
</template>

<script lang="ts" setup>
    import { computed, onBeforeUnmount, onMounted } from 'vue'
    import { RouterView, useRoute } from 'vue-router'
    import { useI18n } from 'vue-i18n'
    import * as locales from '@nuxt/ui/locale'

    import LoaderComponent from '@/components/LoaderComponent.vue'
    import Blank from '@/layouts/Blank.vue'
    import Default from '@/layouts/Default.vue'
    import { useAppStore } from '@/stores/app'
    import type { UserData } from '@/types/User'

    const route = useRoute()
    const { locale } = useI18n()
    const appStore = useAppStore()

    // Keep Nuxt UI's built-in component texts in sync with the vue-i18n locale.
    // Fall back to English if the locale is not shipped by @nuxt/ui/locale.
    const uiLocale = computed(() => (locales as unknown as Record<string, typeof locales.en>)[locale.value] ?? locales.en)

    const layouts = { Default, Blank }
    const layout = computed(() => layouts[(route.meta.layout as keyof typeof layouts) ?? 'Default'])

    const onWindowResized = () => {
        appStore.resizeHandler()
    }

    /**
     * localStorage is attacker- and bug-writable: it can hold malformed JSON, or
     * the literal `null`. An uncaught throw here aborts the rest of onMounted,
     * and a null user object crashes every consumer that reads a name off it.
     */
    const setUserDataFromLocalStorage = () => {
        const userDataString = localStorage.getItem('userData')
        if (!userDataString || userDataString === 'undefined') return

        try {
            const parsed = JSON.parse(userDataString)
            if (parsed && typeof parsed === 'object') appStore.setUserData(parsed as UserData)
        } catch {
            localStorage.removeItem('userData')
        }
    }

    onMounted(() => {
        setUserDataFromLocalStorage()
        onWindowResized()
        window.addEventListener('resize', onWindowResized)
        setTimeout(() => {
            const element = document.getElementById('loading-bg')
            if (element) element.style.display = 'none'
        }, 50)
    })

    onBeforeUnmount(() => {
        window.removeEventListener('resize', onWindowResized)
    })
</script>
